import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  MessageSquare, Home, BarChart3, FileText, Sparkles, Trophy, Award,
  Target, TrendingUp, CheckCircle2, ExternalLink, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

export default function ParentDashboard() {
  const { t, lang, user } = useApp();
  const [data, setData] = useState(null);
  const [reportFor, setReportFor] = useState(null); // {child, content, loading}

  const reload = useCallback(() => {
    api.get('/me/dashboard').then(r => setData(r.data)).catch(() => setData({ children: [], totals: {} }));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const genReport = async (child) => {
    setReportFor({ child, content: '', loading: true });
    try {
      const { data: rep } = await api.post(`/ai/analyze-performance/${child.id}?language=${lang}`);
      setReportFor({ child, content: rep.content, loading: false });
    } catch (e) {
      toast.error(lang === 'ar' ? 'تعذّر إنشاء التقرير' : 'Failed to generate');
      setReportFor(null);
    }
  };

  if (!data) return <main id="main" className="p-8 text-center text-muted-foreground" data-testid="parent-loading">{t('loading')}</main>;

  const children = data.children || [];
  const totals = data.totals || {};

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="parent-dashboard">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">{lang === 'ar' ? `مرحباً ${user?.full_name}` : `Welcome ${user?.full_name}`}</h1>
          <p className="text-muted-foreground mt-1 text-sm">{lang === 'ar' ? 'تابع تقدم أبنائك في برنامج التربية البدنية المعدّلة.' : 'Follow your children\'s progress in the adapted PE program.'}</p>
        </div>
        <Button asChild variant="outline" data-testid="parent-open-messages">
          <Link to="/messages"><MessageSquare className="w-4 h-4 me-1" />{lang === 'ar' ? 'مراسلة المعلم' : 'Message teacher'}</Link>
        </Button>
      </div>

      {/* Family KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <KPI icon={Target} label={lang === 'ar' ? 'أبنائي' : 'My children'} value={totals.student_count || 0} testid="parent-kpi-students" />
        <KPI icon={TrendingUp} label={lang === 'ar' ? 'مجموع النقاط' : 'Total points'} value={totals.points || 0} color="text-amber-600" testid="parent-kpi-points" />
        <KPI icon={Trophy} label={lang === 'ar' ? 'الشارات' : 'Badges'} value={totals.badge_count || 0} color="text-purple-600" testid="parent-kpi-badges" />
        <KPI icon={Award} label={lang === 'ar' ? 'الشهادات' : 'Certificates'} value={totals.certificate_count || 0} color="text-emerald-600" testid="parent-kpi-certs" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Children list */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="font-bold text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" />{lang === 'ar' ? 'أبنائي' : 'My children'}</h2>
          {children.length === 0 ? (
            <Card><CardContent className="p-12 text-center">
              <Target className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">{lang === 'ar' ? 'لم يتم ربط أي طفل بحسابك بعد. تواصل مع المعلم أو الإدارة لربط ملف طفلك (يتم الربط عبر البريد الإلكتروني للوالد).' : 'No children linked yet. Contact the teacher/admin to link your child (matched by parent email).'}</p>
            </CardContent></Card>
          ) : (
            children.map(c => <ChildCard key={c.id} child={c} onReport={() => genReport(c)} />)
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Home className="w-5 h-5" />{lang === 'ar' ? 'أنشطة منزلية مقترحة' : 'Home activities'}</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {[
                { ar: 'تمارين التوازن على قدم واحدة (3×30 ثانية)', en: 'Single-leg balance (3×30s)' },
                { ar: 'مشي ٢٠ دقيقة بصحبة الأسرة', en: '20-min family walk' },
                { ar: 'لعبة رمي الكيس في الإطار (10 محاولات)', en: 'Bean bag toss (10 tries)' },
                { ar: 'تنفّس عميق قبل النوم - 5 دقائق', en: 'Deep breathing before bed - 5 min' },
              ].map((a, i) => (
                <div key={i} className="text-sm p-2 border border-border rounded bg-white" data-testid={`home-activity-${i}`}>
                  • {lang === 'ar' ? a.ar : a.en}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><FileText className="w-5 h-5" />{lang === 'ar' ? 'تواصل مع المدرسة' : 'Stay connected'}</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Button asChild variant="outline" size="sm" className="w-full justify-start">
                <Link to="/messages"><MessageSquare className="w-4 h-4 me-2" />{lang === 'ar' ? 'فتح المحادثة' : 'Open chat'}</Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="w-full justify-start">
                <Link to="/curriculum"><Target className="w-4 h-4 me-2" />{lang === 'ar' ? 'تصفّح المنهج' : 'Browse curriculum'}</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Report dialog */}
      <Dialog open={!!reportFor} onOpenChange={(v) => !v && setReportFor(null)}>
        <DialogContent className="max-w-2xl" data-testid="parent-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />{lang === 'ar' ? 'تقرير أداء AI' : 'AI performance report'}</DialogTitle>
            <DialogDescription>{reportFor?.child?.full_name_ar}</DialogDescription>
          </DialogHeader>
          {reportFor?.loading ? (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{lang === 'ar' ? 'جارٍ تحليل أداء الطفل…' : 'Analyzing performance…'}</p>
            </div>
          ) : (
            <div className="prose-sm max-h-[60vh] overflow-y-auto whitespace-pre-wrap bg-secondary/30 rounded-lg p-4 text-sm leading-relaxed" data-testid="parent-report">
              {reportFor?.content}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}

function ChildCard({ child, onReport }) {
  const { lang } = useApp();
  const iep = child.ieps?.[0]; // primary plan
  const allCerts = child.certificates || [];
  return (
    <Card className="overflow-hidden" data-testid={`child-${child.id}`}>
      <CardContent className="p-0">
        {/* Header strip */}
        <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-transparent p-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-accent grid place-items-center text-white font-black text-xl shadow">
                {child.full_name_ar?.[0] || 'S'}
              </div>
              <div>
                <div className="font-black text-lg">{child.full_name_ar}</div>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <Badge variant="secondary" className="text-[10px]">{child.disability_type}</Badge>
                  <Badge variant="outline" className="text-[10px]">{child.disability_level}</Badge>
                  <Badge variant="outline" className="text-[10px]">{child.age_group}</Badge>
                </div>
              </div>
            </div>
            <div className="text-end">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{lang === 'ar' ? 'النقاط' : 'Points'}</div>
              <div className="text-2xl font-black text-primary stat-number">{child.points || 0}</div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {iep ? (
            <>
              <div>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="font-bold text-muted-foreground flex items-center gap-1"><Target className="w-3.5 h-3.5" />{lang === 'ar' ? 'التقدم في الخطة الفردية' : 'IEP overall progress'}</span>
                  <span className="font-black text-primary stat-number">{iep.overall_progress}%</span>
                </div>
                <Progress value={iep.overall_progress} className="h-3" />
                <div className="flex items-center justify-between text-[11px] mt-1.5 text-muted-foreground">
                  <span><Trophy className="w-3 h-3 inline text-emerald-600 me-0.5" />{iep.mastered_count} {lang === 'ar' ? 'متقن' : 'mastered'}</span>
                  <span>{iep.total_goals} {lang === 'ar' ? 'هدف' : 'goals'} · {lang === 'ar' ? 'حد الإتقان' : 'mastery'} {iep.mastery_threshold}%</span>
                </div>
              </div>

              {/* Per-goal progress */}
              {iep.goals && iep.goals.length > 0 && (
                <div className="space-y-1.5" data-testid={`child-goals-${child.id}`}>
                  <div className="text-xs font-bold text-muted-foreground">{lang === 'ar' ? 'تفاصيل الأهداف' : 'Goal breakdown'}</div>
                  {iep.goals.slice(0, 4).map(g => (
                    <div key={g.goal_id} className="border border-border rounded p-2 bg-white">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          {g.mastered && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                          <Badge variant="secondary" className="font-mono text-[9px] shrink-0">{g.goal_code}</Badge>
                          <span className="text-xs font-medium line-clamp-1">{lang === 'ar' ? g.title_ar : (g.title_en || g.title_ar)}</span>
                        </div>
                        <span className={`text-xs font-bold shrink-0 ${g.mastered ? 'text-emerald-600' : 'text-primary'}`}>{g.best}%</span>
                      </div>
                      <Progress value={g.best} className="h-1.5" />
                    </div>
                  ))}
                  {iep.goals.length > 4 && (
                    <div className="text-[10px] text-muted-foreground text-center pt-1">
                      +{iep.goals.length - 4} {lang === 'ar' ? 'هدف آخر' : 'more goals'}
                    </div>
                  )}
                </div>
              )}

              {/* Recent certs */}
              {allCerts.length > 0 && (
                <div>
                  <div className="text-xs font-bold text-muted-foreground mb-1.5 flex items-center gap-1">
                    <Award className="w-3.5 h-3.5 text-amber-600" />
                    {lang === 'ar' ? 'آخر الشهادات' : 'Recent certificates'}
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {allCerts.slice(0, 3).map(c => (
                      <div key={c.id} className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white rounded-lg p-2 min-w-[140px]" data-testid={`child-cert-${c.id}`}>
                        <Award className="w-4 h-4 text-amber-600 mb-1" />
                        <Badge variant="secondary" className="font-mono text-[9px] mb-1">{c.goal_code}</Badge>
                        <div className="text-[10px] font-semibold line-clamp-2">{c.achievement_ar}</div>
                        <div className="text-[10px] font-bold text-emerald-700 mt-0.5">{c.score}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">{lang === 'ar' ? 'لا توجد خطة فردية بعد. تواصل مع المعلم لإنشاء خطة.' : 'No IEP yet. Contact the teacher to create one.'}</p>
          )}

          {/* Actions */}
          <div className="flex gap-2 flex-wrap pt-2 border-t border-border">
            <Button onClick={onReport} size="sm" className="bg-primary" data-testid={`gen-report-${child.id}`}>
              <Sparkles className="w-4 h-4 me-1" />{lang === 'ar' ? 'تقرير AI' : 'AI report'}
            </Button>
            {iep && (
              <>
                <Button asChild size="sm" variant="outline" data-testid={`view-iep-${child.id}`}>
                  <Link to={`/student-plans/${iep.id}`}><ExternalLink className="w-3.5 h-3.5 me-1" />{lang === 'ar' ? 'عرض الخطة' : 'View IEP'}</Link>
                </Button>
                <Button asChild size="sm" variant="outline" data-testid={`view-certs-${child.id}`}>
                  <Link to={`/student-plans/${iep.id}/certificates`}><Award className="w-3.5 h-3.5 me-1" />{lang === 'ar' ? 'الشهادات' : 'Certificates'}</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function KPI({ icon: Icon, label, value, color = 'text-primary', testid }) {
  return (
    <Card className="card-elevate" data-testid={testid}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
          <Icon className={`w-4 h-4 ${color}`} aria-hidden="true" />
        </div>
        <div className={`text-2xl font-black ${color} stat-number`}>{value}</div>
      </CardContent>
    </Card>
  );
}
